import React, { useState } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "motion/react";
import {
  FaMapMarkerAlt,
  FaPhone,
  FaEnvelope,
  FaClock,
  FaExternalLinkAlt,
  FaTimes,
  FaSearchPlus,
  FaShieldAlt,
  FaUserCheck,
  FaBuilding,
  FaSubway,
  FaComments,
  FaAward
} from "react-icons/fa";
import "../Styles/OurOffice.css";
import Reveal, { RevealGroup, RevealItem } from "./Reveal";
import MagneticButton from "./MagneticButton";

export default function OurOffice() {
  const [selectedImage, setSelectedImage] = useState(null);

  const bentoItems = [
    {
      id: 1,
      slotClass: "bento-slot-1",
      title: "Vidya Education Abroad Headquarters",
      tag: "Main Reception & Entrance",
      image: process.env.PUBLIC_URL + "/Office.jpg",
      description:
        "Our prime headquarters located in Andheri West, Mumbai. Designed to welcome students and parents with a transparent, highly professional overseas consultation environment."
    },
    {
      id: 2,
      slotClass: "bento-slot-2",
      title: "Senior Counselor Executive Suite",
      tag: "Executive Suite",
      image: process.env.PUBLIC_URL + "/OfficeCabin.jpg",
      description:
        "Dedicated executive cabin where senior counselors conduct detailed profile evaluations, university shortlisting, and career roadmap counseling."
    },
    {
      id: 3,
      slotClass: "bento-slot-3",
      title: "Student Consultation Suite A",
      tag: "Counselor Cabin",
      image: process.env.PUBLIC_URL + "/OfficeCabin2.jpg",
      description:
        "Private consultation space for family discussions, financial planning audits, and mock visa interview preparation."
    },
    {
      id: 4,
      slotClass: "bento-slot-4",
      title: "Student Application Workspace",
      tag: "Counselor Cabin",
      image: process.env.PUBLIC_URL + "/OfficeCabin3.jpg",
      description:
        "Equipped desk space dedicated to online application submissions, statement of purpose (SOP) reviews, and document verification."
    },
    {
      id: 5,
      slotClass: "bento-slot-5",
      title: "VEA Official Accreditation & Brand Wall",
      tag: "Official Accreditation Wall",
      image: process.env.PUBLIC_URL + "/OfficeWall.jpg",
      description:
        "Our official company wall showcasing agency accreditations, verified partner university credentials, and service guarantees."
    }
  ];

  return (
    <div className="office-page">
      <Helmet>
        <title>Vidya Education Abroad Office | Mumbai HQ & Counseling Hub</title>
        <meta
          name="description"
          content="Explore Vidya Education Abroad official office in Mumbai. Tour our counselor cabins, documentation desks, and meet expert study abroad advisors in person."
        />
        <meta
          name="keywords"
          content="Vidya Education Abroad office, VEA Mumbai office, study abroad consultancy office Andheri, foreign education counselor office, study abroad office address Mumbai"
        />
        <link rel="canonical" href="https://veabroad.com/office" />
      </Helmet>

      {/* Hero Section */}
      <section className="office-hero">
        <div className="office-hero-blob office-hero-blob-1" />
        <div className="office-hero-blob office-hero-blob-2" />

        <div className="container position-relative z-2">
          <div className="row align-items-center">
            <div className="col-lg-7">
              <Reveal direction="up" duration={0.55} className="office-hero-badge">
                <FaBuilding /> Official Headquarters & Student Support Center
              </Reveal>
              <Reveal as="h1" direction="up" delay={0.1} className="office-hero-title">
                Welcome to <span className="highlight-text">Vidya Education Abroad</span> Office
              </Reveal>
              <Reveal as="p" direction="up" delay={0.2} className="office-hero-subtitle">
                Visit our physical headquarters in Mumbai. Experience 100% transparent in-person
                counseling, document verification, and personalized overseas education guidance
                from certified advisors.
              </Reveal>
              <Reveal direction="up" delay={0.3} className="d-flex flex-wrap gap-3">
                <a
                  href="https://maps.app.goo.gl/VMFggTPEdmD7gcmSA"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="gmaps-btn"
                >
                  <FaMapMarkerAlt /> Get Directions on Google Maps
                </a>
                <MagneticButton as="span">
                  <Link to="/consultationForm" className="cta-secondary-btn">
                    Book In-Person Consultation
                  </Link>
                </MagneticButton>
              </Reveal>
            </div>

            <div className="col-lg-5">
              <Reveal direction="zoom" delay={0.2} className="hero-img-card">
                <img
                  src={process.env.PUBLIC_URL + "/Office.jpg"}
                  alt="Vidya Education Abroad Office Entrance"
                />
                <div className="hero-img-badge">
                  <FaBuilding />
                  <span>Official Headquarters • Andheri West, Mumbai</span>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Stats Bar */}
      <div className="container office-stats-container">
        <RevealGroup as="div" className="office-stats-bar" stagger={0.1}>
          <div className="row g-4">
            <div className="col-6 col-md-3">
              <RevealItem direction="up" className="office-stat-item">
                <div className="office-stat-icon-wrapper">
                  <FaBuilding />
                </div>
                <div className="office-stat-info">
                  <div className="office-stat-number">Mumbai HQ</div>
                  <div className="office-stat-label">Andheri West</div>
                </div>
              </RevealItem>
            </div>

            <div className="col-6 col-md-3">
              <RevealItem direction="up" className="office-stat-item">
                <div className="office-stat-icon-wrapper">
                  <FaUserCheck />
                </div>
                <div className="office-stat-info">
                  <div className="office-stat-number">100%</div>
                  <div className="office-stat-label">In-Person Support</div>
                </div>
              </RevealItem>
            </div>

            <div className="col-6 col-md-3">
              <RevealItem direction="up" className="office-stat-item">
                <div className="office-stat-icon-wrapper">
                  <FaShieldAlt />
                </div>
                <div className="office-stat-info">
                  <div className="office-stat-number">Physical HQ</div>
                  <div className="office-stat-label">Counseling Center</div>
                </div>
              </RevealItem>
            </div>

            <div className="col-6 col-md-3">
              <RevealItem direction="up" className="office-stat-item">
                <div className="office-stat-icon-wrapper">
                  <FaAward />
                </div>
                <div className="office-stat-info">
                  <div className="office-stat-number">500+</div>
                  <div className="office-stat-label">Students Guided</div>
                </div>
              </RevealItem>
            </div>
          </div>
        </RevealGroup>
      </div>

      {/* Unique Bento Grid Photo Collage Showcase */}
      <section className="gallery-section">
        <div className="container">
          <Reveal direction="up" className="text-center mb-4">
            <div className="d-block text-center mb-1">
              <span className="section-badge">Infrastructure Showcase</span>
            </div>
            <h2 className="section-title">Explore Our Office Spaces</h2>
            <p className="section-desc">
              A creative showcase of our counseling suites, executive desks, document
              verification area, and official brand wall. Click any space to inspect in full resolution.
            </p>
          </Reveal>

          {/* Bento Collage Grid */}
          <RevealGroup as="div" className="bento-collage-grid" stagger={0.08}>
            {bentoItems.map((item) => (
              <RevealItem
                as="div"
                direction="zoom"
                key={item.id}
                className={`bento-card ${item.slotClass}`}
                onClick={() => setSelectedImage(item)}
              >
                <img src={item.image} alt={item.title} className="bento-img" loading="lazy" />
                <div className="bento-overlay">
                  <span className="bento-tag">{item.tag}</span>
                  <h3 className="bento-title">{item.title}</h3>
                  <p className="bento-desc">{item.description}</p>
                </div>
                <div className="bento-zoom-btn">
                  <FaSearchPlus />
                </div>
              </RevealItem>
            ))}
          </RevealGroup>
        </div>
      </section>

      {/* Lightbox Modal */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="lightbox-modal"
            onClick={() => setSelectedImage(null)}
          >
            <motion.div
              initial={{ scale: 0.85, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.85, y: 20 }}
              className="lightbox-content"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="lightbox-close-btn"
                onClick={() => setSelectedImage(null)}
                aria-label="Close modal"
              >
                <FaTimes />
              </button>
              <div className="lightbox-img-container">
                <img src={selectedImage.image} alt={selectedImage.title} />
              </div>
              <div className="lightbox-body">
                <span className="bento-tag" style={{ color: "#36948c" }}>{selectedImage.tag}</span>
                <h3 className="fs-4 fw-bold text-dark mt-1 mb-2">{selectedImage.title}</h3>
                <p className="text-muted mb-0 fs-6">{selectedImage.description}</p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Key Facilities Section */}
      <section className="features-section position-relative">
        <div className="va-blob-field">
          <span className="va-blob va-blob-teal" style={{ width: 300, height: 300, top: "-6%", left: "-5%" }} />
          <span className="va-blob va-blob-navy" style={{ width: 260, height: 260, bottom: "-8%", right: "-4%" }} />
        </div>
        <div className="container position-relative z-2">
          <Reveal direction="up" className="text-center mb-5">
            <div className="d-block text-center mb-1">
              <span className="section-badge">Built For Excellence</span>
            </div>
            <h2 className="section-title"><span className="va-shine-text">World-Class</span> Counseling Environment</h2>
            <p className="section-desc">
              Every section of our office is crafted to ensure student privacy, clear communication,
              and dedicated support at every phase of your application.
            </p>
          </Reveal>

          <RevealGroup as="div" className="row g-4" stagger={0.1}>
            <div className="col-md-6 col-lg-3">
              <RevealItem direction="up" className="feature-card va-hover-lift">
                <div className="feature-icon-box">
                  <FaComments />
                </div>
                <h3>Private Counselor Cabins</h3>
                <p>
                  Acoustic-isolated cabins for quiet, 1-on-1 sessions to discuss university choices,
                  budget requirements, and career roadmaps with zero distraction.
                </p>
              </RevealItem>
            </div>

            <div className="col-md-6 col-lg-3">
              <RevealItem direction="up" className="feature-card va-hover-lift">
                <div className="feature-icon-box">
                  <FaShieldAlt />
                </div>
                <h3>Document Verification Desk</h3>
                <p>
                  Dedicated desk for in-person auditing of academic marksheets, financial documents,
                  work experience letters, and passport validity.
                </p>
              </RevealItem>
            </div>

            <div className="col-md-6 col-lg-3">
              <RevealItem direction="up" className="feature-card va-hover-lift">
                <div className="feature-icon-box">
                  <FaBuilding />
                </div>
                <h3>Executive Lounge & Reception</h3>
                <p>
                  Welcoming reception space with university catalogs, program guides, and comfortable
                  seating for visiting students and families.
                </p>
              </RevealItem>
            </div>

            <div className="col-md-6 col-lg-3">
              <RevealItem direction="up" className="feature-card va-hover-lift">
                <div className="feature-icon-box">
                  <FaAward />
                </div>
                <h3>Certified Advisors</h3>
                <p>
                  Direct access to accredited study abroad counselors, visa application experts, and
                  test preparation guides.
                </p>
              </RevealItem>
            </div>
          </RevealGroup>
        </div>
      </section>

      {/* Location Chart & Interactive Map Section */}
      <section className="location-section" id="location-map">
        <div className="container">
          <Reveal direction="up" className="text-center mb-5">
            <div className="d-block text-center mb-1">
              <span className="section-badge">Easy Access</span>
            </div>
            <h2 className="section-title">Location Chart & Directions</h2>
            <p className="section-desc">
              Situated in the heart of Andheri West, Mumbai, our office is easily accessible via metro,
              railway, and major arterial roads.
            </p>
          </Reveal>

          <div className="location-grid">
            {/* Location Info Card */}
            <Reveal direction="left" className="location-info-card">
              <div>
                <div className="location-header-tag">
                  <FaMapMarkerAlt /> Mumbai Headquarters
                </div>
                <h3 className="location-title">Vidya Education Abroad</h3>

                <div className="location-details-list">
                  <div className="detail-item">
                    <div className="detail-icon">
                      <FaMapMarkerAlt />
                    </div>
                    <div className="detail-content">
                      <label>Address</label>
                      <p>Amar Jyoti CHS, SV Road, Andheri West, Mumbai, Maharashtra 400058</p>
                    </div>
                  </div>

                  <div className="detail-item">
                    <div className="detail-icon">
                      <FaSubway />
                    </div>
                    <div className="detail-content">
                      <label>Landmark & Transport</label>
                      <p>Near Andheri Railway Station & Azad Nagar Metro Station</p>
                    </div>
                  </div>

                  <div className="detail-item">
                    <div className="detail-icon">
                      <FaPhone />
                    </div>
                    <div className="detail-content">
                      <label>Phone Contact</label>
                      <p><a href="tel:+918850285886">+91 8850285886</a></p>
                    </div>
                  </div>

                  <div className="detail-item">
                    <div className="detail-icon">
                      <FaEnvelope />
                    </div>
                    <div className="detail-content">
                      <label>Email Address</label>
                      <p><a href="mailto:info@veabroad.com">info@veabroad.com</a></p>
                    </div>
                  </div>

                  <div className="detail-item">
                    <div className="detail-icon">
                      <FaClock />
                    </div>
                    <div className="detail-content">
                      <label>Working Hours</label>
                      <p>Monday – Saturday: 10:00 AM – 7:00 PM <br />(Sunday: By Prior Appointment)</p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <a
                  href="https://maps.app.goo.gl/VMFggTPEdmD7gcmSA"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="gmaps-btn w-100"
                >
                  <FaExternalLinkAlt /> Open Location on Google Maps
                </a>
              </div>
            </Reveal>

            {/* Interactive Map Embed with Red Marker Pin on Exact Address */}
            <Reveal direction="right" delay={0.1} className="map-wrapper">
              <iframe
                title="Vidya Education Abroad Google Map Location"
                src="https://maps.google.com/maps?q=Amar%20Jyoti%20CHS%2C%20SV%20Road%2C%20Andheri%20West%2C%20Mumbai%2C%20Maharashtra%20400058&t=&z=16&ie=UTF8&iwloc=&output=embed"
                loading="lazy"
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Why Visit In Person */}
      <section className="why-visit-section">
        <div className="container">
          <Reveal direction="up" className="text-center mb-5">
            <div className="d-block text-center mb-1">
              <span className="section-badge">Transparency & In-Person Trust</span>
            </div>
            <h2 className="section-title">Why In-Person Visits Matter</h2>
            <p className="section-desc">
              Visiting our physical office gives you complete confidence, direct advisor access, and
              uncompromising clarity for your abroad education plans.
            </p>
          </Reveal>

          <RevealGroup as="div" className="row g-4" stagger={0.12}>
            <div className="col-md-4">
              <RevealItem direction="up" className="trust-card va-hover-lift">
                <div className="trust-card-number">01</div>
                <h4>Face-to-Face Transparency</h4>
                <p>
                  Meet your counselor in person. Discuss university choices, tuition costs, and visa
                  requirements openly without hidden terms.
                </p>
              </RevealItem>
            </div>

            <div className="col-md-4">
              <RevealItem direction="up" className="trust-card va-hover-lift">
                <div className="trust-card-number">02</div>
                <h4>Instant Document Audit</h4>
                <p>
                  Hand over physical marksheets and financial proofs for on-the-spot verification and
                  immediate eligibility assessment.
                </p>
              </RevealItem>
            </div>

            <div className="col-md-4">
              <RevealItem direction="up" className="trust-card va-hover-lift">
                <div className="trust-card-number">03</div>
                <h4>Complete Peace of Mind</h4>
                <p>
                  Verify our physical office infrastructure, meet our senior directors, and build an
                  enduring trust relationship for your career.
                </p>
              </RevealItem>
            </div>
          </RevealGroup>
        </div>
      </section>

      {/* CTA Section */}
      <section className="office-cta-section">
        <div className="container">
          <Reveal direction="up" className="office-cta-box">
            <h2 className="office-cta-title">Plan Your Visit to Our Mumbai Office</h2>
            <p className="office-cta-desc">
              Book a personalized 1-on-1 counseling session with senior advisors at Vidya Education Abroad
              headquarters.
            </p>
            <div className="cta-btn-group">
              <MagneticButton as="span">
                <Link to="/consultationForm" className="cta-primary-btn va-shimmer-btn">
                  Schedule Office Appointment
                </Link>
              </MagneticButton>
              <a href="tel:+918850285886" className="cta-secondary-btn">
                <FaPhone className="me-2" /> Call +91 8850285886
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
